import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import { toast } from "react-toastify";
import { ColumnConfig } from "../types/ColumnConfig";
import { formatToDDMMYYYY } from "../utils/Validation";
import DateInput from "../components/DateInput";
import DropdownSearch from "../components/DropDown";
import Search from "../components/SearchInput";
import "./ScanHistory.css";
import "./Common.css";
import { getScanHistory, getUserListForFilters } from "../services/apiService";
import ExporterButton from "../components/ExportButton";
interface User {
    userId: number;
    firstName: string;
    lastName: string;
    userEmail: string;
    userMobile: string;
    userType: number;
    isActive: boolean;
    blockLevel: string;
    profilePhotoUrl: string | null;
}

interface ScanHistoryItem {
    id: number;
    userId: number;
    serialNumber: string;
    points: number;
    scanStatus: string;
    scanMessage: string;
    skuCode: string;
    ip: string;
    source: string;
    longitude: string;
    latitude: string;
    createdAt: string;
    user: User;
}

const ScanHistory = () => {
    const [data, setData] = useState<ScanHistoryItem[]>([]);
    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        fromDate: null as Date | null,
        toDate: null as Date | null,
        scanStatus: "",
        source: "",
        skuCode: "",
        userId: "",
        serialNumber: "",
    });

    const [userList, setUserList] = useState<{ userId: number; userName: string }[]>([]);
    const [userLoading, setUserLoading] = useState(false);

    useEffect(() => {
        const fetchUserList = async () => {
            setUserLoading(true);
            try {
                const response = await getUserListForFilters();
                if (response?.data) {
                    setUserList(response?.data || []);
                }
            } catch (error) {
                console.error("Error fetching user list", error);
                toast.error("Failed to load user list");
            } finally {
                setUserLoading(false);
            }
        };
        fetchUserList();
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            const areDatesValid =
                (filters.fromDate && filters.toDate) ||
                (!filters.fromDate && !filters.toDate);

            if (areDatesValid) {
                loadData();
            }
        }, 800);
        return () => clearTimeout(handler);
    }, [filters]);

    const getApiParams = () => {
        const apiParams: any = {};
        if (filters.fromDate) apiParams.fromDate = filters.fromDate.toISOString();
        if (filters.toDate) {
            const tempDate = new Date(filters.toDate);
            tempDate.setHours(23, 59, 59, 999);
            apiParams.toDate = tempDate.toISOString();
        }
        if (filters.scanStatus) apiParams.scanStatus = filters.scanStatus;
        if (filters.source) apiParams.source = filters.source;
        if (filters.skuCode.trim()) apiParams.skuCode = filters.skuCode.trim();
        if (filters.serialNumber.trim()) apiParams.serialNumber = filters.serialNumber.trim();
        
        if (filters.userId && filters.userId.trim()) {
            const userIdsArray = filters.userId.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
            if (userIdsArray.length > 0) {
                apiParams.userId = userIdsArray.join(',');
            }
        }
        return apiParams;
    };

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await getScanHistory(getApiParams());
            if (response?.data) {
                setData(response.data);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error("Error loading scan history data", error);
            toast.error("Error loading scan history data");
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const fileExporter = async () => {
        try {
            const response = await getScanHistory(getApiParams());

            if (response?.data && response.data.length > 0) {
                toast.success("Exported successfully.");
                return response.data.map((item: any) => ({
                    ...item,
                    userName: item.user ? `${item.user.firstName || ''} ${item.user.lastName || ''}`.trim() : '',
                    userMobile: item.user?.userMobile || '',
                    userEmail: item.user?.userEmail || ''
                }));
            } else {
                toast.error("No data available to export.");
                return [];
            }
        } catch (error) {
            toast.error("Failed to fetch export data.");
            return [];
        }
    };

    const columns: ColumnConfig[] = [
        {
            field: "userName",
            headerName: "User Name",
            width: 180,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const user = params.row.user;
                return `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || '-';
            }
        },
        {
            field: "userMobile",
            headerName: "Mobile",
            width: 140,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return params.row.user?.userMobile || '-';
            }
        },
        {
            field: "userEmail",
            headerName: "Email",
            width: 200,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div style={{
                        maxWidth: '190px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} title={params.row.user?.userEmail || ''}>
                        {params.row.user?.userEmail || '-'}
                    </div>
                );
            }
        },
        {
            field: "serialNumber",
            headerName: "Serial Number",
            width: 200,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div style={{
                        maxWidth: '190px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} title={params.value || ''}>
                        {params.value || '-'}
                    </div>
                );
            }
        },
        {
            field: "skuCode",
            headerName: "SKU Code",
            width: 140,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: "points",
            headerName: "Points",
            width: 100,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <span style={{
                        fontWeight: '600',
                        color: '#2e7d32'
                    }}>
                        {params.value || 0}
                    </span>
                );
            }
        },
        {
            field: "scanStatus",
            headerName: "Status",
            width: 120,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                const status = params.value?.toLowerCase();
                return (
                    <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor:
                            status === 'success' ? '#d4edda' :
                                status === 'failed' ? '#f8d7da' :
                                    status === 'pending' ? '#fff3cd' : 'transparent',
                        color:
                            status === 'success' ? '#155724' :
                                status === 'failed' ? '#721c24' :
                                    status === 'pending' ? '#856404' : 'inherit',
                        fontSize: '12px',
                        fontWeight: '500'
                    }}>
                        {params.value?.charAt(0).toUpperCase() + params.value?.slice(1)}
                    </span>
                );
            }
        },
        {
            field: "scanMessage",
            headerName: "Message",
            width: 180,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return (
                    <div style={{
                        maxWidth: '170px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }} title={params.value || ''}>
                        {params.value || '-'}
                    </div>
                );
            }
        },
        {
            field: "source",
            headerName: "Source",
            width: 100,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return params.value?.toUpperCase() || '-';
            }
        },
        {
            field: "ip",
            headerName: "IP Address",
            width: 130,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center'
        },
        {
            field: "createdAt",
            headerName: "Scan Date",
            width: 150,
            editable: false,
            sortable: false,
            headerClassName: "custom-header",
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => {
                return params.value ? formatToDDMMYYYY(params.value) : '-';
            }
        },
    ];

    const handleInput = (e: string, type: string) => {
        setFilters((prev) => ({ ...prev, [type]: e }));
    };

    const handleDateInput = (date: any, type: string) => {
        setFilters((prev) => ({ ...prev, [type]: date }));
    };

    const userDropdownOptions = userList.map((user) => ({
        label: user.userName,
        value: user.userId.toString(),
    }));

    const statusDropdownOptions = [
        { label: "Success", value: "success" },
        { label: "Failed", value: "failed" },
    ];

    const sourceDropdownOptions = [
        { label: "App", value: "app" },
        { label: "Web", value: "web" },
    ];

    return (
        <div>
            <div
                style={{
                    padding: "20px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                }}
            >
                <span className="scan-history-title">Scan History</span>

                <div
                    className="scan-history-header"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        width: "100%",
                    }}
                >
                    <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginTop: 10, marginBottom: 20 }}>
                        <DropdownSearch
                            searchTxt={filters.userId}
                            handleChange={(e: any) => handleInput(e, "userId")}
                            placeholder="Select User"
                            label="User Search"
                            data={userDropdownOptions}
                            searchable={true}
                            cleanable={true}
                        />
                        <DropdownSearch
                            searchTxt={filters.scanStatus}
                            handleChange={(e: any) => handleInput(e, "scanStatus")}
                            placeholder="Select Status"
                            label="Scan Status"
                            data={statusDropdownOptions}
                            searchable={true}
                            cleanable={true}
                        />
                        <DropdownSearch
                            searchTxt={filters.source}
                            handleChange={(e: any) => handleInput(e, "source")}
                            placeholder="Select Source"
                            label="Source"
                            data={sourceDropdownOptions}
                            searchable={true}
                            cleanable={true}
                        />
                        <Search
                            searchTxt={filters.skuCode}
                            handleInput={(e: string) => handleInput(e, "skuCode")}
                            placeholder="Enter SKU Code"
                            label="SKU Code"
                        />
                        <Search
                            searchTxt={filters.serialNumber}
                            handleInput={(e: string) => handleInput(e, "serialNumber")}
                            placeholder="Enter Serial Number"
                            label="Serial Number"
                        />
                        <DateInput
                            handleInput={(date) => handleDateInput(date, "fromDate")}
                            searchTxt={filters.fromDate}
                            placeholder="Select From Date"
                            label="From Date"
                            minDate={filters.toDate}
                        />
                        <DateInput
                            handleInput={(date) => handleDateInput(date, "toDate")}
                            searchTxt={filters.toDate}
                            placeholder="Select To Date"
                            label="To Date"
                            maxDate={filters.fromDate}
                        />
                    </div>
                </div>

                <div className="table-container">
                    <div style={{ display: "flex", justifyContent: "flex-start", padding: "10px 10px 0px 10px" }}>
                        <ExporterButton exporter={fileExporter} reportName="Scan History" />
                    </div>
                    <DataGrid
                        rows={data}
                        columns={columns}
                        autoHeight={true}
                        disableColumnFilter={true}
                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 10,
                                },
                            },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        getRowId={(ele: any) => ele?.id}
                        disableRowSelectionOnClick
                        loading={loading}
                        sx={{
                            "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                                outline: "none !important",
                            },
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default ScanHistory;
